import { X } from 'lucide-react'
import { cn } from '../../lib/cn.js'
import { getFloatingLauncherStyles } from '../../lib/floatingAnchor.js'
import { sanitizeWidgetBubbleIconSvg } from '../../lib/sanitizeBubbleIcon.js'
import { resolveButtonBorderRadius } from '@shared/launcherButtonIcons.jsx'
import { LauncherButtonContent } from './LauncherButtonContent.jsx'
import { OchatbotBubbleIcon } from './OchatbotBubbleIcon.jsx'

const bubbleButtonClass = cn(
  'flex h-14 w-14 items-center justify-center',
  'rounded-full shadow-[var(--shadow-widget)] hover:scale-105',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)] focus-visible:ring-offset-2',
)

const focusRingClass =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)] focus-visible:ring-offset-2'

/**
 * Resolve which floating launcher to show. `widgetType` (unified envelope)
 * wins; the legacy flat `layout` field is a fallback.
 */
function resolveFloatingLauncherKind(conf) {
  const wt = String(conf?.widgetType || '').trim().toLowerCase()
  if (wt === 'button') return 'button'
  if (wt === 'bubble' || wt === 'icon') return 'bubble'
  if (wt === 'search') return 'search'
  const layout = String(conf?.layout || '').trim().toLowerCase()
  if (layout === 'button') return 'button'
  if (layout === 'bubble') return 'bubble'
  if (layout === 'search') return 'search'
  return 'bubble'
}

export { resolveFloatingLauncherKind }

/**
 * Colors the launcher currently renders with (bubble fill/background or
 * button bg/fg, depending on `widgetType`). Shared with `FloatingInlineMessage`
 * so the free-shipping/abandoned-cart slideout matches the launcher exactly.
 */
export function resolveFloatingLauncherColors(conf) {
  const launcherKind = resolveFloatingLauncherKind(conf || {})
  const brand = conf?.widgetButtonColor?.trim?.() || 'var(--color-brand)'
  const brandFg =
    conf?.widgetButtonTextColor?.trim?.() || 'var(--color-brand-foreground)'

  if (launcherKind === 'button') {
    return {
      background: conf?.buttonColor?.trim?.() || brand,
      foreground: conf?.buttonTextColor?.trim?.() || brandFg,
    }
  }

  return {
    background:
      conf?.widget_bubble_background_color ||
      conf?.buttonColor?.trim?.() ||
      brand ||
      'var(--color-brand)',
    foreground:
      conf?.widget_bubble_color ||
      conf?.buttonTextColor?.trim?.() ||
      brandFg ||
      'var(--color-brand-foreground)',
  }
}

/**
 * Floating launcher (bubble icon or configured text button). The actual chat
 * panel is rendered as a sibling so we can animate them independently and so
 * the Radix Tooltip/Dialog primitives can portal into the shadow root without
 * the launcher's transforms breaking their positioning math.
 */
export function ChatLauncher({ open, onToggle, label = 'Chat with us', conf }) {
  const launcherKind = resolveFloatingLauncherKind(conf || {})
  const brand = conf?.widgetButtonColor?.trim?.() || 'var(--color-brand)'
  const brandFg =
    conf?.widgetButtonTextColor?.trim?.() || 'var(--color-brand-foreground)'

  const bubbleBg =
    conf?.widget_bubble_background_color ||
    conf?.buttonColor?.trim?.() ||
    brand ||
    'var(--color-brand)'
  const bubbleFg =
    conf?.widget_bubble_color ||
    conf?.buttonTextColor?.trim?.() ||
    brandFg ||
    'var(--color-brand-foreground)'
  const customSvg = sanitizeWidgetBubbleIconSvg(
    conf?.widgetBubbleIcon || conf?.widget_bubble_icon,
  )

  const { positionStyle, colorStyle, usePositionWrapper } = getFloatingLauncherStyles(
    conf || {},
    launcherKind === 'button'
      ? conf?.buttonColor?.trim?.() || brand
      : bubbleBg,
    launcherKind === 'button'
      ? conf?.buttonTextColor?.trim?.() || brandFg
      : bubbleFg,
  )

  const buttonRadius = resolveButtonBorderRadius(conf?.buttonBorderRadius)
  const buttonBg = conf?.buttonColor?.trim?.() || brand
  const buttonFg = conf?.buttonTextColor?.trim?.() || brandFg
  const buttonBorder = conf?.buttonBorderColor?.trim?.() || buttonBg
  const buttonText =
    conf?.buttonText?.trim?.() || conf?.widgetTitle || 'Chat with AI'

  const bubbleIcon = open ? (
    <X className="h-6 w-6" />
  ) : customSvg ? (
    <span
      className="flex h-10 w-10 items-center justify-center [&_svg]:max-h-10 [&_svg]:max-w-10"
      // Admin-configured SVG (same trust model as legacy widget-js).
      dangerouslySetInnerHTML={{ __html: customSvg }}
    />
  ) : (
    <OchatbotBubbleIcon
      className="h-14 w-14 max-h-[60px] max-w-[60px]"
      bubbleFill={bubbleFg}
      bubbleBackground={bubbleBg}
    />
  )

  const button =
    launcherKind === 'button' ? (
      <button
        type="button"
        aria-label={open ? 'Close chat' : buttonText || label}
        onClick={onToggle}
        style={{
          ...(usePositionWrapper ? {} : positionStyle),
          backgroundColor: buttonBg,
          color: buttonFg,
          borderColor: buttonBorder,
          borderRadius: open ? '9999px' : buttonRadius,
        }}
        className={cn(
          focusRingClass,
          'inline-flex items-center justify-center gap-2 border border-solid shadow-[var(--shadow-widget)] transition-opacity hover:opacity-90',
          open
            ? 'h-14 w-14'
            : 'min-w-[160px] px-5 py-2.5 text-[15px] font-medium',
        )}
      >
        {open ? (
          <X className="h-6 w-6" />
        ) : (
          <LauncherButtonContent conf={conf} />
        )}
      </button>
    ) : (
      <button
        type="button"
        aria-label={label}
        onClick={onToggle}
        style={usePositionWrapper ? colorStyle : { ...positionStyle, ...colorStyle }}
        className={bubbleButtonClass}
      >
        {bubbleIcon}
      </button>
    )

  if (usePositionWrapper) {
    return (
      <span style={positionStyle} className="inline-block">
        {button}
      </span>
    )
  }

  return button
}
